import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore,
  type CSSProperties, type KeyboardEvent, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from '../../../icons';
import { useModalLeave } from '../../../hooks/useModalLeave';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../../../lib/shared-i18n';
import './Combobox.css';

/**
 * Combobox — custom dropdown 컴포넌트 (W3C WAI-ARIA 1.2 Combobox pattern).
 *
 * 본 cycle (옵션 B Phase D) 첫 도입. native `<select>` 의 a11y/animation 한계 우회.
 * 본 cycle 은 1 페이지 (TaskListView) 채택, 다른 7 페이지의 native select 마이그는
 * 별도 cycle.
 *
 * features:
 *   - 단일 선택 (multi-select deferred)
 *   - searchable (선택) — 입력 글자로 options filter
 *   - 키보드: Tab/↑↓/Enter/Esc/Home/End
 *   - 마운트 transition: opacity + translateY (--duration-fast)
 *   - reduced-motion: tokens.css global gate 가 자동 처리
 *
 * a11y:
 *   - role="combobox" + aria-expanded + aria-controls + aria-activedescendant
 *   - listbox role="listbox", option role="option" + aria-selected
 *   - close 시 trigger 로 focus 복귀
 *
 * 비특징 (deferred):
 *   - multi-select / chip
 *   - option group (label 분류)
 *   - async options (debounced search)
 *   - virtualized list (대량 옵션)
 *
 * ★★ **`portal` 을 켜면 목록이 body 로 뜬다** (2026-09-06 추가, 기본은 꺼짐).
 *    평소에는 트리거 바로 아래 `position:absolute` 인데, **`overflow` 를 가진 조상 안에서는
 *    그 상자에 잘린다.** 메일 환경설정에서 실제로 겪은 것 둘 —
 *      · 모달 본문(`.wc-modal__body { overflow-y:auto }`) 안의 「상위 메일함」 → 목록이
 *        상자 밖으로 나가 **세로 스크롤만 생기고 고를 수가 없었다.**
 *      · 표 상자(`.wc-table-wrap { overflow-x:auto }`) 안의 「보관기간」 → 같은 증상.
 *        (한 축이 `auto` 면 나머지 축의 `visible` 도 `auto` 로 계산된다 — CSS 규칙이다.)
 *    켜면 `position:fixed` 로 body 에 그리고 **트리거 자리를 재서** 좌표를 정한다.
 *    아래가 모자라면 위로 열고, 옆이 모자라면 안쪽으로 당기고, 스크롤·창 크기 변경을 따라간다.
 *    ★ 기본이 꺼짐인 이유 — 지금 쓰는 90여 곳의 그리는 자리를 한꺼번에 바꾸지 않으려는 것이다.
 */

export interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  value: string | null;
  onChange: (value: string) => void;
  options: ReadonlyArray<ComboboxOption>;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  /** trigger 의 aria-label. 미지정 시 placeholder 사용. */
  ariaLabel?: string;
  /** 기본 className 외 추가. */
  className?: string;
  /** option 의 표시 형식 커스터마이징. default 는 label. */
  renderOption?: (option: ComboboxOption) => ReactNode;

  /* ── 아래 넷은 2026-09-01 추가(채택 2단계 3순위). 전부 선택이고 **안 주면 종전과 같다** —
   *    `Select` 가 FormField 의 배선(라벨·오류·도움말)을 여기로 흘려보내려고 쓴다. ── */
  /** 트리거의 id. 라벨의 `htmlFor` 와 짝이 된다. */
  id?: string;
  /** 도움말·오류를 가리키는 id 들. */
  describedBy?: string;
  /** 오류 상태 — 테두리와 `aria-invalid` 가 함께 바뀐다. */
  invalid?: boolean;
  required?: boolean;

  /**
   * 목록을 **body 로 띄운다** (2026-09-06 추가). `overflow` 를 가진 상자
   * (모달 본문 · 표 상자) 안에 설 때 켠다 — 안 켜면 그 상자에 잘린다.
   * ★ 기본은 꺼짐이라 **안 주면 종전과 똑같다.**
   */
  portal?: boolean;
}

/** 창 가장자리에서 이만큼은 띄운다. */
const EDGE = 8;
/** 트리거와 목록 사이. */
const GAP = 4;
/** 아래가 이만큼도 안 남으면 위로 연다. */
const MIN_ROOM = 160;

export function Combobox({
  value, onChange, options, placeholder, disabled = false,
  searchable = false, ariaLabel, className, renderOption,
  id, describedBy, invalid = false, required = false, portal = false,
}: ComboboxProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  // Cycle 72 — default placeholder i18n 적용. props 미전달 시 lang-aware '선택'/Select/選択.
  const effectivePlaceholder = placeholder ?? tShared('shared.combobox.default_placeholder');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<number>(-1);
  const [query, setQuery] = useState('');
  // unmount transition — open=false 후 150ms 까지 popover 가 leaving 상태로 잔존.
  const { shouldRender: popoverRender, isLeaving: popoverLeaving } = useModalLeave(open, 150);

  /** `portal` 일 때만 쓰는 좌표. 꺼져 있으면 늘 null 이라 종전 경로 그대로다. */
  const [fixedStyle, setFixedStyle] = useState<CSSProperties | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const componentId = useId();
  const listboxId = `${componentId}-listbox`;

  // searchable 시 query 로 options filter. 미지정 시 모든 options.
  const filtered = useMemo(() => {
    if (!searchable || query.trim() === '') return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [searchable, query, options]);

  const selectedOption = options.find((o) => o.value === value) ?? null;

  // close 시 trigger 로 focus 복귀.
  const closeAndFocusTrigger = useCallback(() => {
    setOpen(false);
    setQuery('');
    triggerRef.current?.focus();
  }, []);

  // open 시 첫 highlighted 위치 = selected 또는 0.
  useEffect(() => {
    if (open) {
      const idx = filtered.findIndex((o) => o.value === value);
      setHighlighted(idx >= 0 ? idx : (filtered.length > 0 ? 0 : -1));
      // searchable 인 경우 input 으로 focus.
      if (searchable) {
        // 마운트 직후 focus — opacity transition 과 race 회피 위해 raf.
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    }
  }, [open, filtered, value, searchable]);

  // 외부 click → close.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (
        triggerRef.current?.contains(target) === false &&
        listboxRef.current?.contains(target) === false &&
        inputRef.current?.contains(target) === false
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  /**
   * `portal` 일 때의 좌표 — **트리거 자리를 재서** 정한다.
   *
   * ★ 아래가 모자라면 위로 연다. 옆이 모자라면 창 안쪽으로 당긴다.
   * ★ 높이는 남은 자리로 못 박는다 — 안 그러면 목록이 창 밖으로 흘러 끝이 안 보인다.
   */
  const place = useCallback(() => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const below = window.innerHeight - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const up = below < MIN_ROOM && above > below;
    const width = r.width > 0 ? r.width : undefined;
    const left = width === undefined
      ? EDGE
      : Math.max(EDGE, Math.min(r.left, window.innerWidth - width - EDGE));
    setFixedStyle(up
      ? { left, width, bottom: window.innerHeight - r.top + GAP, maxHeight: Math.max(MIN_ROOM, above) }
      : { left, width, top: r.bottom + GAP, maxHeight: Math.max(MIN_ROOM, below) });
  }, []);

  /* 스크롤·창 크기 변경을 «따라간다» — 닫아 버리면 모달 본문을 조금만 굴려도 목록이 사라진다.
     scroll 은 capture 로 듣는다(모달 본문처럼 «안쪽» 상자가 구르는 경우가 이 기능의 이유다). */
  useLayoutEffect(() => {
    if (!portal || !popoverRender) return;
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [portal, popoverRender, place]);

  const selectAt = useCallback((idx: number) => {
    const opt = filtered[idx];
    if (!opt || opt.disabled) return;
    onChange(opt.value);
    closeAndFocusTrigger();
  }, [filtered, onChange, closeAndFocusTrigger]);

  const onTriggerKeyDown = (ev: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (ev.key === 'ArrowDown' || ev.key === 'Enter' || ev.key === ' ') {
      ev.preventDefault();
      setOpen(true);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setOpen(true);
    } else if (ev.key === 'Escape' && open) {
      // ★ 2026-09-15 — 찾기 칸이 없으면 열어도 포커스가 트리거에 남는다. 여기서 Esc 를 안 받으면
      //   목록은 그대로고 바깥 모달만 닫혔다. 열려 있을 때만 먹는다(닫혀 있으면 모달 Esc 가 그대로 돈다).
      ev.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const onListKeyDown = (ev: KeyboardEvent<HTMLElement>) => {
    if (!open) return;
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setHighlighted((cur) => (cur + 1) % Math.max(1, filtered.length));
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setHighlighted((cur) => (cur - 1 + filtered.length) % Math.max(1, filtered.length));
    } else if (ev.key === 'Home') {
      ev.preventDefault();
      setHighlighted(0);
    } else if (ev.key === 'End') {
      ev.preventDefault();
      setHighlighted(filtered.length - 1);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      if (highlighted >= 0) selectAt(highlighted);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      closeAndFocusTrigger();
    } else if (ev.key === 'Tab') {
      // Tab 으로 close — focus 는 자연 흐름 따름 (closeAndFocusTrigger 안 함).
      setOpen(false);
      setQuery('');
    }
  };

  /**
   * ★★★ **밖에 라벨이 있으면 `aria-label` 을 달지 않는다.**
   *   `aria-label` 은 `<label for>` 을 **이긴다** — 둘 다 있으면 읽어 주는 이름이
   *   눈에 보이는 라벨(「휴가 종류」)이 아니라 자리표시자(「선택」)가 된다.
   *   그러면 화면에 라벨을 붙여 놓고도 **소리로는 그 이름이 사라진다.**
   *   `id` 는 `FormField` 가 라벨과 짝지어 줄 때만 온다 — 그때만 비운다.
   *   (지금 Combobox 를 쓰는 18곳은 `id` 를 안 주므로 종전 그대로다.)
   */
  const labelledOutside = id != null && ariaLabel == null;
  const selfLabel = labelledOutside ? undefined : (ariaLabel ?? effectivePlaceholder);

  const cn = [
    'wc-combobox',
    open ? 'wc-combobox--open' : '',
    invalid ? 'wc-combobox--invalid' : '',
    className ?? '',
  ].filter(Boolean).join(' ');
  const activeId = highlighted >= 0 && filtered[highlighted]
    ? `${componentId}-opt-${filtered[highlighted].value}` : undefined;

  const popover = popoverRender ? (
    <div
      className={[
        'wc-combobox__popover',
        popoverLeaving ? 'wc-combobox__popover--leaving' : '',
        portal ? 'wc-combobox__popover--fixed' : '',
      ].filter(Boolean).join(' ')}
      style={portal ? (fixedStyle ?? undefined) : undefined}
      role="presentation"
      aria-hidden={popoverLeaving || undefined}
    >
      {searchable && (
        <input
          ref={inputRef}
          type="text"
          className="wc-combobox__search"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setHighlighted(0); }}
          onKeyDown={onListKeyDown}
          placeholder={tShared('shared.combobox.search_placeholder')}
          aria-controls={listboxId}
          aria-activedescendant={activeId}
          tabIndex={popoverLeaving ? -1 : 0}
        />
      )}
      <ul
        ref={listboxRef}
        id={listboxId}
        role="listbox"
        className="wc-combobox__listbox"
        tabIndex={popoverLeaving ? -1 : (searchable ? -1 : 0)}
        onKeyDown={searchable ? undefined : onListKeyDown}
        aria-label={selfLabel}
        aria-labelledby={labelledOutside ? id : undefined}
      >
        {filtered.length === 0 && (
          <li className="wc-combobox__empty" role="presentation">{tShared('shared.combobox.empty')}</li>
        )}
        {filtered.map((o, idx) => {
          const isSelected = o.value === value;
          const isHighlighted = idx === highlighted;
          const optCn = [
            'wc-combobox__option',
            isSelected ? 'wc-combobox__option--selected' : '',
            isHighlighted ? 'wc-combobox__option--highlighted' : '',
            o.disabled ? 'wc-combobox__option--disabled' : '',
          ].filter(Boolean).join(' ');
          return (
            <li
              key={o.value}
              id={`${componentId}-opt-${o.value}`}
              role="option"
              className={optCn}
              aria-selected={isSelected}
              aria-disabled={o.disabled || undefined}
              onMouseEnter={() => setHighlighted(idx)}
              onClick={() => selectAt(idx)}
            >
              {renderOption ? renderOption(o) : o.label}
            </li>
          );
        })}
      </ul>
    </div>
  ) : null;

  return (
    <div className={cn}>
      <button
        ref={triggerRef}
        type="button"
        className="wc-combobox__trigger"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeId}
        aria-label={selfLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className={selectedOption ? 'wc-combobox__value' : 'wc-combobox__placeholder'}>
          {selectedOption ? (renderOption ? renderOption(selectedOption) : selectedOption.label) : effectivePlaceholder}
        </span>
        {/* ★ 2026-09-01 — 글자 `▾` 를 아이콘으로 바꿨다. 글자는 글꼴마다 크기·위치가 달라
            같은 화살표가 화면마다 다르게 보였다(정본 B4: --icon-sm · --text-muted · 열림 시 180°). */}
        <ChevronDown className="wc-combobox__chevron" aria-hidden />
      </button>

      {/* ★ body 로 띄우는 경우에만 포털을 탄다 — 안 켜면 지금까지처럼 트리거 옆에 그린다. */}
      {portal ? (popover && createPortal(popover, document.body)) : popover}
    </div>
  );
}
